import { defineStore } from 'pinia'
import { markRaw } from 'vue'

// 图层数据结构
class Layer {
  constructor({ id, url, img, scale, offsetX, offsetY, rotation, isText, textContent, textFont, textColor, fontSize }) {
    this.id = id
    this.url = url || null
    this.img = img ? markRaw(img) : null  // Three.js 纹理图片
    this.scale = scale || 1
    this.offsetX = offsetX || 0
    this.offsetY = offsetY || 0
    this.rotation = rotation || 0
    this.isText = isText || false
    this.textContent = textContent || ''
    this.textFont = textFont || 'Arial'
    this.textColor = textColor || '#000000'
    this.fontSize = fontSize || 80
  }
}

export const useDesignStore = defineStore('design', {
  state: () => ({
    // 当前状态
    currentGarment: 'Short Sleeve',
    currentSelectedPart: 'front',
    currentEditingLogo: null,  // { part, id, isText }
    currentDesignId: null,
    
    // 设计数据
    userColors: new Map(),      // part -> colorHex (number)
    partLayersMap: new Map(),   // part -> Layer[]
    textItems: [],              // 冗余存储，便于 TextEditor 组件使用
    
    allImageGallery: [],
    // ID 管理
    nextId: 1,
    
    // 所有款式的设计数据（用于切换服装时保留）
    garmentDesigns: {
      'Short Sleeve': { colors: new Map(), layers: new Map(), texts: [], nextId: 1 },
      'Long Sleeve': { colors: new Map(), layers: new Map(), texts: [], nextId: 1 },
      'Cycling Vest': { colors: new Map(), layers: new Map(), texts: [], nextId: 1 },
      'Mx Jersey': { colors: new Map(), layers: new Map(), texts: [], nextId: 1 },
      'Hoodie': { colors: new Map(), layers: new Map(), texts: [], nextId: 1 },
      'T-shirt': { colors: new Map(), layers: new Map(), texts: [], nextId: 1 },
      'Baseball Jersey': { colors: new Map(), layers: new Map(), texts: [], nextId: 1 }
    },
    rosterList: [],
    garmentDesignsLoaded: false,
    _isLoadingDesign: false,
  }),
  
  getters: {
    // 获取当前款式的所有图层
    currentLayersMap: (state) => state.partLayersMap,
    
    // 获取当前款式的颜色
    currentColors: (state) => state.userColors,
    
    // 获取指定部位的图层
    getPartLayers: (state) => (part) => state.partLayersMap.get(part) || [],
    
    // 获取指定部位的基准颜色
    getPartBaseColor: (state) => (part) => state.userColors.get(part) || 0xffffff
  },
  
  
  actions: {


async loadSharedDesign(designId, options = {}) {
  console.log('开始加载设计:', designId)
  
  // 设置默认参数
  const targetGarment = options.targetGarment || null
  const loadComments = options.loadComments || false
  
  try {
    // 1. 请求数据
    const response = await fetch(`/load-design.php?id=${designId}`)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const result = await response.json()
    
    if (!result.success || !result.all_garments) {
      throw new Error('加载设计失败')
    }
    
    // 2. 确定显示哪个款式
    let finalGarment = targetGarment
    if (!finalGarment) {
      finalGarment = result.current_garment || 'Short Sleeve'
    }
    
    // 3. 恢复所有款式数据（颜色、文字、Logo）
    for (const [garmentType, garmentData] of Object.entries(result.all_garments)) {
      if (!this.garmentDesigns[garmentType]) {
        this.garmentDesigns[garmentType] = { colors: new Map(), layers: new Map(), texts: [], nextId: 1 }
      }
      
      const design = this.garmentDesigns[garmentType]
      
      // 恢复颜色
      design.colors.clear()
      if (garmentData.colors) {
        for (const [part, colorHex] of Object.entries(garmentData.colors)) {
          const colorNum = typeof colorHex === 'string' 
            ? parseInt(colorHex.substring(1), 16) 
            : colorHex
          design.colors.set(part, colorNum)
        }
      }
      
      // 恢复文字
      if (garmentData.texts && garmentData.texts.length > 0) {
        design.texts = garmentData.texts.map(t => ({ ...t }))
      }
    }
    
    // 4. 切换到目标款式
    this.switchGarment(finalGarment)
    
    // 5. 恢复 Roster 名单
    if (result.roster && result.roster.length > 0) {
      this.rosterList = result.roster.map(m => ({
        name: m.name || '',
        gender: m.gender || 'Male',
        style: m.style || finalGarment,
        size: m.size != null ? m.size : '',
        quantity: m.quantity != null && m.quantity !== '' ? m.quantity : '',
        message: m.message || ''
      }))
    } else {
      this.rosterList = []
    }
    
    console.log('设计加载完成:', designId)
    return { success: true, result, finalGarment }
    
  } catch (error) {
    console.error('加载设计失败:', error)
    throw error
  }
},



async saveRosterToServer() {
  if (!this.currentDesignId) return; // 如果没有设计ID，不进行自动保存
  
  // 1. 提取当前的 roster 数据结构
  const rosterData = [];
  // 假设你的 rosterList 存储在 store 中，或者需要从组件传过来。
  // 如果你的 roster 还在 RosterManager 局部组件管理，可以接受一个参数：saveRosterToServer(rosterList)
  // 这里我们采用更健壮的“组件传参”方式，修改方法声明如下：
},

async updateRosterToRemote(rosterList) {
  if (!this.currentDesignId) return;
  
  try {
    const response = await fetch('https://www.customteamkit.com/php/save-design.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        design_id: this.currentDesignId,
        roster: rosterList // 只更新最新的名单数据，保留PHP里的全量设计数据
      })
    });
    const data = await response.json();
    if (!data.success) {
      console.error('Auto-save roster failed:', data.message);
    } else {
      console.log('Roster auto-saved successfully.');
    }
  } catch (error) {
    console.error('Network error during roster auto-save:', error);
  }
},
    
    // ========== 核心操作方法 ==========
    
    appendTextItem(textItem) {
      console.log('appendTextItem called:', textItem)
      
      // ★★★ 确保 imgSrc 存在 ★★★
      let finalImgSrc = textItem.imgSrc
      if (!finalImgSrc && textItem.img && textItem.img.src) {
        finalImgSrc = textItem.img.src
      }
      
      // 如果还是没有，使用 _createThumbCanvas 生成缩略图
      if (!finalImgSrc && textItem.text && textItem.font && textItem.color) {
        finalImgSrc = this._createThumbCanvas(textItem.text, textItem.font, textItem.color, textItem.fontSize || 80)
      }
      
      const newItem = { ...textItem, imgSrc: finalImgSrc }
      console.log('appendTextItem - final imgSrc:', finalImgSrc ? finalImgSrc.substring(0, 100) : 'none')
      
      // 使用解构赋值重新覆盖整个数组
      this.textItems = [...this.textItems, newItem]
      console.log('after append, textItems length:', this.textItems.length)
    },

    updateRoster(list) {
      this.rosterList = [...list]
    },

    /**
     * 添加文字到指定部位
     */
    async addTextToPart(part, textItem) {
      const newId = (textItem.id !== null && textItem.id !== undefined) ? textItem.id : this.nextId++;
      
      // ★★★ 关键修复：优先使用传入的 imgSrc（来自 TextEditor 的高清缩略图） ★★★
      let textImgSrc = textItem.imgSrc || '';
      let textImg = textItem.img;
      
      // 如果没有传入 img，则生成贴图用的 Canvas 图像
      if (!textImg && textItem.text && textItem.font && textItem.color) {
        const canvas = this._createTextCanvas(textItem.text, textItem.font, textItem.color, textItem.fontSize || 80);
        textImg = new Image();
        textImg.src = canvas.toDataURL();
        // 注意：这里不覆盖 textImgSrc，保留传入的高清缩略图
      }
      
      // ★★★ 如果还是没有 imgSrc，则生成一个用于列表显示的缩略图 ★★★
      if (!textImgSrc && textItem.text && textItem.font && textItem.color) {
        textImgSrc = this._createThumbCanvas(textItem.text, textItem.font, textItem.color, textItem.fontSize || 80);
      }

      const newLayer = new Layer({
        id: newId,
        img: textImg,
        scale: textItem.scale || 0.8,
        offsetX: textItem.offsetX || 0,
        offsetY: textItem.offsetY || 0,
        rotation: textItem.rotation || 0,
        isText: true,
        textContent: textItem.text,
        textFont: textItem.font,
        textColor: textItem.color,
        fontSize: textItem.fontSize || 80
      });

      // 更新 partLayersMap
      let layers = this.partLayersMap.get(part) || [];
      // 深度拷贝并推入新图层，防止 Vue 2 无法追踪数组内部突变
      layers = [...layers, newLayer];
      this.partLayersMap.set(part, layers);
      this.partLayersMap = new Map(this.partLayersMap);

      // ★★★ 创建新的文字项，确保 imgSrc 正确传递 ★★★
      const newTextItem = {
        id: newId,
        text: textItem.text,
        font: textItem.font,
        color: textItem.color,
        part: part,
        scale: newLayer.scale,
        offsetX: newLayer.offsetX,
        offsetY: newLayer.offsetY,
        fontSize: newLayer.fontSize,
        rotation: newLayer.rotation,
        img: newLayer.img,
        imgSrc: textImgSrc  // 使用已确保存在的缩略图
      };

      // ★★★ 添加调试日志 ★★★
      console.log('创建新的文字项:', newTextItem)
      console.log('imgSrc 值:', newTextItem.imgSrc ? newTextItem.imgSrc.substring(0, 80) + '...' : 'none')

      // 使用方法添加到文字项列表
      this.appendTextItem(newTextItem);

      // ★★★ 强制触发响应式更新 ★★★
      this.textItems = [...this.textItems]

      this.currentEditingLogo = { part, id: newId, isText: true };
      if (window.$viewer3D) {
        window.$viewer3D.currentEditingLogo = { part, id: newId, isText: true };
        window.$viewer3D.compositePartTexture(part, newId);
        window.$viewer3D.showTextureControls();
      }

      return newLayer;
    },
    
    /**
     * 添加图片到指定部位
     */
    addImageToPart(part, imageUrl, imageId = null) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        
        img.onload = () => {
          const newLayer = new Layer({
            id: imageId || this.nextId++,
            url: imageUrl,
            img: img,
            scale: 1.0,
            offsetX: 0,
            offsetY: 0,
            rotation: 0,
            isText: false
          })
          
          let layers = this.partLayersMap.get(part) || []
          layers = [...layers, newLayer]
          this.partLayersMap.set(part, layers)
          this.partLayersMap = new Map(this.partLayersMap)

          this.currentEditingLogo = { part, id: newLayer.id, isText: false };

          if (window.$viewer3D) {
            window.$viewer3D.currentEditingLogo = { part, id: newLayer.id, isText: false }
            window.$viewer3D.compositePartTexture(part, newLayer.id)
            window.$viewer3D.showTextureControls()
          }

          resolve(newLayer)
        }
        
        img.onerror = reject
        img.src = imageUrl
      })
    },
    
    /**
     * 更新图层属性（位置、缩放、旋转）
     */
    updateLayerProperty(part, layerId, prop, value) {
      const layers = this.partLayersMap.get(part)
      if (!layers) return
      
      const targetLayer = layers.find(l => l.id === layerId)
      if (targetLayer) {
        targetLayer[prop] = value
        
        // 如果是文字，同步到 textItems
        if (targetLayer.isText) {
          // 采用浅拷贝强制刷新数组内对象，触发 Vue 2 对列表项属性的监测
          this.textItems = this.textItems.map(t => {
            if (t.id === layerId) {
              return { ...t, [prop]: value }
            }
            return t
          })
        }
        
        // 强制刷新整个 Map 引用
        this.partLayersMap = new Map(this.partLayersMap)
      }
    },
    
    /**
     * 删除图层
     */
    deleteLayer(part, layerId) {
      const layers = this.partLayersMap.get(part)
      if (!layers) return
      
      const targetLayer = layers.find(l => l.id === layerId)
      const isText = targetLayer?.isText || false
      
      const newLayers = layers.filter(l => l.id !== layerId)
      if (newLayers.length === 0) {
        this.partLayersMap.delete(part)
      } else {
        this.partLayersMap.set(part, newLayers)
      }
      
      if (isText) {
        this.textItems = this.textItems.filter(t => t.id !== layerId)
      }
      
      this.partLayersMap = new Map(this.partLayersMap)
      
      // 如果删除的是当前选中的图层，清空选中状态
      if (this.currentEditingLogo?.id === layerId) {
        this.currentEditingLogo = null
      }
    },
    
    /**
     * 调整图层顺序
     */
    reorderLayers(part, fromIndex, toIndex) {
      const layers = this.partLayersMap.get(part)
      if (!layers || fromIndex === toIndex) return
      
      const newLayers = [...layers]
      const [movedLayer] = newLayers.splice(fromIndex, 1)
      newLayers.splice(toIndex, 0, movedLayer)
      
      this.partLayersMap.set(part, newLayers)
      this.partLayersMap = new Map(this.partLayersMap)
    },
    
    /**
     * 设置部位颜色
     */
    setPartColor(part, hexColor) {
      const colorNum = typeof hexColor === 'string' 
        ? parseInt(hexColor.substring(1), 16) 
        : hexColor

      if (part === 'whole') {
        const allParts = ['front', 'back', 'collar', 'left sleeve', 'right sleeve',
                          'left side', 'right side', 'pocket', 'hood']
        allParts.forEach(p => {
          this.userColors.set(p, colorNum)
        })
        if (window.$viewer3D && window.$viewer3D.partsMap) {
          window.$viewer3D.partsMap.forEach((material, p) => {
            this.userColors.set(p, colorNum)
          })
        }
      } else {
        this.userColors.set(part, colorNum)
      }
      this.userColors = new Map(this.userColors)

      if (window.$viewer3D && typeof window.$viewer3D.applyColorsToModel === 'function') {
        window.$viewer3D.applyColorsToModel()
      } else {
        if (window.$viewer3D && window.$viewer3D.partsMap) {
          if (part === 'whole') {
            window.$viewer3D.partsMap.forEach((material) => {
              material.color.set(colorNum)
              material.needsUpdate = true
            })
          } else {
            const material = window.$viewer3D.partsMap.get(part)
            if (material) {
              material.color.set(colorNum)
              material.needsUpdate = true
            }
          }
        }
      }
    },

    
    checkGarmentDataIntegrity(garmentType) {
      const design = this.garmentDesigns[garmentType]
      if (!design) return false
      
      // 检查数据完整性
      let hasData = false
      
      // 检查颜色
      if (design.colors && design.colors.size > 0) {
        hasData = true
      }
      
      // 检查图层
      if (design.layers && design.layers.size > 0) {
        for (const layers of design.layers.values()) {
          if (layers && layers.length > 0) {
            hasData = true
            break
          }
        }
      }
      
      // 检查文字
      if (design.texts && design.texts.length > 0) {
        hasData = true
      }
      
      return hasData
    },

    // 强制同步当前款式数据到 garmentDesigns
    forceSyncCurrentGarmentData() {
      if (!this.garmentDesigns[this.currentGarment]) {
        this.garmentDesigns[this.currentGarment] = { colors: new Map(), layers: new Map(), texts: [], nextId: 1 }
      }
      
      const design = this.garmentDesigns[this.currentGarment]
      
      // 同步颜色
      design.colors.clear()
      for (const [part, color] of this.userColors.entries()) {
        design.colors.set(part, color)
      }
      
      // 同步图层
      design.layers.clear()
      for (const [part, layers] of this.partLayersMap.entries()) {
        const savedLayers = layers.map(l => ({
          id: l.id,
          url: l.url,
          scale: l.scale,
          offsetX: l.offsetX,
          offsetY: l.offsetY,
          rotation: l.rotation,
          isText: l.isText,
          textContent: l.textContent,
          textFont: l.textFont,
          textColor: l.textColor,
          fontSize: l.fontSize
        }))
        design.layers.set(part, savedLayers)
      }
      
      // ★★★ 关键修复：同步文字时确保每个项都有 imgSrc ★★★
      design.texts = this.textItems.map(t => {
        if (t.imgSrc) {
          return { ...t }
        }
        const imgSrc = this._createThumbCanvas(
          t.text || t.textContent,
          t.font || t.textFont,
          t.color || t.textColor,
          t.fontSize || 80
        )
        return { ...t, imgSrc }
      })
      design.nextId = this.nextId
      
      console.log(`强制同步款式 ${this.currentGarment} 数据完成:`, {
        colors: design.colors.size,
        layers: design.layers.size,
        texts: design.texts.length
      })
    },
    /**
     * 切换服装款式
     */
    switchGarment(garmentType) {
      console.log('[v0] Store: switchGarment called', garmentType, 'from', this.currentGarment)
      
      // 如果切换到同一款式，直接返回
      if (this.currentGarment === garmentType) {
        console.log('[v0] Same garment, skipping switch')
        return
      }
      
      // 切换前先自动保存上一次编辑的旧款式状态数据
      if (this.currentGarment) {
        console.log('[v0] Saving current garment data before switch:', this.currentGarment)
        console.log('[v0] Current textItems before save:', this.textItems.length, this.textItems.map(t => ({ id: t.id, text: t.text })))
        this.saveCurrentGarmentData()
        console.log('[v0] Saved garment data:', this.currentGarment, 'texts:', this.garmentDesigns[this.currentGarment]?.texts?.length)
      }

      if (!this.garmentDesigns[garmentType]) {
        this.garmentDesigns[garmentType] = {
          colors: new Map(),
          layers: new Map(),
          texts: [],
          nextId: 1
        }
      }

      const design = this.garmentDesigns[garmentType]
      console.log('[v0] Target garment design:', garmentType, 'texts:', design.texts?.length, design.texts?.map(t => ({ id: t.id, text: t.text })))
      
      // 恢复 colors - 创建新的 Map 实例触发响应式
      const newColors = new Map()
      if (design.colors) {
        for (const [part, color] of design.colors.entries()) {
          newColors.set(part, color)
        }
      }
      this.userColors = newColors
      
      // 恢复 layers - 创建新的 Map 实例触发响应式
      const newLayersMap = new Map()
      if (design.layers) {
        for (const [part, layers] of design.layers.entries()) {
          const restoredLayers = []
          for (const l of layers) {
            const newLayerInstance = new Layer({
              id: l.id,
              url: l.url,
              scale: l.scale,
              offsetX: l.offsetX,
              offsetY: l.offsetY,
              rotation: l.rotation,
              isText: l.isText,
              textContent: l.textContent,
              textFont: l.textFont,
              textColor: l.textColor,
              fontSize: l.fontSize
            })
            
            if (!l.isText && l.url) {
              const img = new Image()
              img.crossOrigin = 'anonymous'
              img.src = l.url
              newLayerInstance.img = markRaw(img)
            }
            
            if (l.isText && l.textContent && l.textFont && l.textColor) {
              const canvas = this._createTextCanvas(
                l.textContent, 
                l.textFont, 
                l.textColor, 
                l.fontSize || 80
              )
              const img = new Image()
              img.src = canvas.toDataURL()
              newLayerInstance.img = markRaw(img)
            }
            
            restoredLayers.push(newLayerInstance)
          }
          newLayersMap.set(part, restoredLayers)
        }
      }
      this.partLayersMap = newLayersMap
      
      // 恢复 texts - 确保每个项都有 imgSrc，并创建全新数组触发响应式
      const newTextItems = (design.texts || []).map(text => {
        // 如果已经有 imgSrc，直接使用
        if (text.imgSrc) {
          return { ...text }
        }
        // 否则生成缩略图
        const imgSrc = this._createThumbCanvas(
          text.text || text.textContent,
          text.font || text.textFont,
          text.color || text.textColor,
          text.fontSize || 80
        )
        return { ...text, imgSrc }
      })
      
      // ★★★ 关键修复：先设置 currentGarment，再设置 textItems ★★★
      // 这样可以确保 TextEditor 的 watch 在 textItems 变化时能够正确响应
      this.currentGarment = garmentType
      this.currentSelectedPart = 'front'
      
      // 恢复 nextId
      this.nextId = design.nextId || 1
      this.textItems = []
      // 最后设置 textItems，触发 TextEditor 的响应式更新
      this.textItems = newTextItems
      // 强制触发全局更新事件
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('garment-switched', { 
          detail: { garment: garmentType, textItems: newTextItems }
        }))
      }
        this.textItems = [...this.textItems]
      console.log('[v0] Store: Switched to', garmentType, 
        'userColors size:', this.userColors.size,
        'partLayersMap size:', this.partLayersMap.size,
        'textItems count:', this.textItems.length,
        'textItems:', this.textItems.map(t => ({ id: t.id, text: t.text })))
    },

    /**
     * 保存当前款式的数据到 garmentDesigns
     */
    saveCurrentGarmentData() {
      const design = this.garmentDesigns[this.currentGarment]
      if (design) {
        design.colors = new Map(this.userColors)
        design.layers = new Map()
        for (const [part, layers] of this.partLayersMap.entries()) {
          // 只保存纯数据可序列化字段
          design.layers.set(part, layers.map(l => ({
            id: l.id,
            url: l.url,
            scale: l.scale,
            offsetX: l.offsetX,
            offsetY: l.offsetY,
            rotation: l.rotation,
            isText: l.isText,
            textContent: l.textContent,
            textFont: l.textFont,
            textColor: l.textColor,
            fontSize: l.fontSize
          })))
        }
        // ★★★ 关键修复：保存时确保每个文字项都有 imgSrc ★★★
        design.texts = this.textItems.map(t => {
          // 如果已经有 imgSrc，直接保留
          if (t.imgSrc) {
            return { ...t }
          }
          // 否则生成缩略图
          const imgSrc = this._createThumbCanvas(
            t.text || t.textContent,
            t.font || t.textFont,
            t.color || t.textColor,
            t.fontSize || 80
          )
          return { ...t, imgSrc }
        })
        design.nextId = this.nextId
        
        console.log('saveCurrentGarmentData:', this.currentGarment, 
          'texts count:', design.texts.length,
          'texts with imgSrc:', design.texts.filter(t => t.imgSrc).length)
      }
    },
    
    setCurrentSelectedPart(part) {
      console.log('Store: setCurrentSelectedPart', part)
      this.currentSelectedPart = part
    },
    
    clearLogoSelection() {
      this.currentEditingLogo = null
    },
    
    selectLogo(part, id, isText) {
      console.log('Store: selectLogo called', { part, id, isText })
      this.currentEditingLogo = { part, id, isText }
      // 强制触发响应式更新（创建新对象）
      this.currentEditingLogo = { ...this.currentEditingLogo }
    },
    
    rebuildAllImageGallery() {
      const allUrls = new Set()
      for (const garment of Object.values(this.garmentDesigns)) {
        if (!garment.layers) continue
        for (const layers of garment.layers.values()) {
          if (!Array.isArray(layers)) continue
          for (const layer of layers) {
            if (!layer.isText && layer.url) {
              allUrls.add(layer.url)
            }
          }
        }
      }
      this.allImageGallery = Array.from(allUrls).map(url => ({
        id: Date.now() + Math.random() * 10000,
        url: url
      }))
    },

    /**
     * 从后端加载的数据恢复所有款式设计
     */
    loadDesignFromData(data) {
      console.log('loadDesignFromData called:', data)
      if (!data || !data.all_garments) return
      
      for (const [garmentType, garmentData] of Object.entries(data.all_garments)) {
        if (!this.garmentDesigns[garmentType]) {
          this.garmentDesigns[garmentType] = { colors: new Map(), layers: new Map(), texts: [], nextId: 1 }
        }
        
        const design = this.garmentDesigns[garmentType]
        
        // 恢复颜色
        design.colors.clear()
        if (garmentData.colors) {
          for (const [part, colorHex] of Object.entries(garmentData.colors)) {
            const colorNum = typeof colorHex === 'string' 
              ? parseInt(colorHex.substring(1), 16) 
              : colorHex
            design.colors.set(part, colorNum)
          }
        }
        
        // 恢复图层并转换为规范格式
        design.layers.clear()
        
        // 恢复 Logos
        if (garmentData.logos && garmentData.logos.length > 0) {
          for (const logo of garmentData.logos) {
            let layers = design.layers.get(logo.part) || []
            layers.push({
              id: logo.id,
              url: logo.url,
              scale: logo.scale || 1,
              offsetX: logo.offsetX || 0,
              offsetY: logo.offsetY || 0,
              rotation: logo.rotation || 0,
              isText: false,
              img: null
            })
            design.layers.set(logo.part, layers)
          }
        }
        
        // 恢复 Texts
        if (garmentData.texts && garmentData.texts.length > 0) {
          for (const text of garmentData.texts) {
            let layers = design.layers.get(text.part) || []
            layers.push({
              id: text.id,
              scale: text.scale || 0.8,
              offsetX: text.offsetX || 0,
              offsetY: text.offsetY || 0,
              rotation: text.rotation || 0,
              isText: true,
              textContent: text.text,
              textFont: text.font,
              textColor: text.color,
              fontSize: text.fontSize || 80,
              img: null
            })
            design.layers.set(text.part, layers)
          }
          // ★★★ 为每个文字项生成缩略图 ★★★
          design.texts = garmentData.texts.map(t => ({
            ...t,
            imgSrc: t.imgSrc || this._createThumbCanvas(t.text, t.font, t.color, t.fontSize || 80)
          }))
        }
        
        let maxId = design.nextId || 1
        if (garmentData.texts) {
          garmentData.texts.forEach(t => { if (t.id && t.id > maxId) maxId = t.id })
        }
        if (garmentData.logos) {
          garmentData.logos.forEach(l => { if (l.id && l.id > maxId) maxId = l.id })
        }
        design.nextId = maxId + 1
      }
      
      const targetGarment = data.current_garment || 'Short Sleeve'
      this.switchGarment(targetGarment)
      this.rebuildAllImageGallery()
    },
    
    /**
     * 同步生成文字 canvas
     */
    _createTextCanvas(text, font, color, fontSize) {
      const canvas = document.createElement('canvas')
      canvas.width = 512
      canvas.height = 512
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      let fs = fontSize || 80
      ctx.font = `Bold ${fs}px "${font}", Arial, sans-serif`
      ctx.fillStyle = color || '#000000'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      const maxWidth = canvas.width - 40
      let textWidth = ctx.measureText(text).width
      while (textWidth > maxWidth && fs > 12) {
        fs -= 5
        ctx.font = `Bold ${fs}px "${font}", Arial, sans-serif`
        textWidth = ctx.measureText(text).width
      }
      ctx.fillText(text.replace(/[\r\n]+/g, ' '), canvas.width / 2, canvas.height / 2)
      return canvas
    },

    /**
     * ★★★ 生成用于列表显示的缩略图 ★★★
     */
    _createThumbCanvas(text, font, color, fontSize) {
      const canvas = document.createElement('canvas')
      canvas.width = 128
      canvas.height = 128
      const ctx = canvas.getContext('2d')
      // 白色背景
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, 128, 128)
      const safeFont = `"${font}", Arial, "Helvetica Neue", sans-serif`
      let fs = 36
      ctx.font = `Bold ${fs}px ${safeFont}`
      // 文字颜色：太浅的颜色（接近白色）换成深色，确保在白底上可见
      const isLightColor = (c) => {
        if (!c || c.length < 7) return false
        const hex = c.replace('#', '')
        const r = parseInt(hex.substr(0,2),16)
        const g = parseInt(hex.substr(2,2),16)
        const b = parseInt(hex.substr(4,2),16)
        return (r*299 + g*587 + b*114) / 1000 > 200
      }
      ctx.fillStyle = color && !isLightColor(color) ? color : '#1e293b'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      const cleanText = (text || 'T').replace(/[\r\n]+/g, ' ')
      const maxW = 118
      let tw = ctx.measureText(cleanText).width
      while (tw > maxW && fs > 8) {
        fs -= 3
        ctx.font = `Bold ${fs}px ${safeFont}`
        tw = ctx.measureText(cleanText).width
      }
      ctx.fillText(cleanText, 64, 64)
      return canvas.toDataURL()
    },

    /**
     * 导出所有款式数据（用于保存到后端数据库）
     */
    exportAllGarmentsData() {
      // 确保当前编辑款式的数据已同步写入到全局映射中
      this.saveCurrentGarmentData()
      
      const allData = {}
      for (const [garment, design] of Object.entries(this.garmentDesigns)) {
        const colors = {}
        design.colors.forEach((colorValue, partName) => {
          const hex = typeof colorValue === 'number' 
            ? colorValue.toString(16).padStart(6, '0') 
            : colorValue
          colors[partName] = '#' + hex
        })
        
        const logos = []
        const texts = []
        for (const [part, layers] of design.layers.entries()) {
          layers.forEach(layer => {
            if (layer.isText) {
              texts.push({
                id: layer.id,
                text: layer.textContent,
                font: layer.textFont,
                color: layer.textColor,
                part: part,
                scale: layer.scale,
                offsetX: layer.offsetX,
                offsetY: layer.offsetY,
                fontSize: layer.fontSize,
                rotation: layer.rotation
              })
            } else if (layer.url) {
              logos.push({
                id: layer.id,
                part: part,
                url: layer.url,
                scale: layer.scale,
                offsetX: layer.offsetX,
                offsetY: layer.offsetY,
                rotation: layer.rotation
              })
            }
          })
        }
        
        allData[garment] = { colors, texts, logos, nextId: design.nextId }
      }
      
      return {
        current_garment: this.currentGarment,
        all_garments: allData
      }
    }
  }
})
